import React, { useState, useEffect } from "react";
import { Search, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { navLinks, brand } from "../../mock/mock";

const SCROLL_THRESHOLD = 30;

const useScrolled = (threshold = SCROLL_THRESHOLD) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const Logo = ({ className }) => (
  <img
    src={brand.logo}
    alt="Overdressed Couture"
    className={className}
  />
);

const DesktopNav = () => (
  <nav className="hidden lg:flex items-center gap-8 flex-1">
    {navLinks.map((l) => (
      <a
        key={l.label}
        href={l.href}
        className="link-underline font-body text-[13px] font-medium text-[#1A1A1A]"
      >
        {l.label}
      </a>
    ))}
  </nav>
);

const HeaderIcons = ({ bagCount, wishCount }) => (
  <div className="flex items-center gap-5 text-[#1A1A1A] lg:flex-1 lg:justify-end">
    <button className="hover:text-[#822432] transition-colors" aria-label="Search">
      <Search size={20} strokeWidth={1.6} />
    </button>
    <button
      className="relative hover:text-[#822432] transition-colors"
      aria-label="Wishlist"
    >
      <Heart size={20} strokeWidth={1.6} />
      {wishCount > 0 && (
        <span className="absolute -top-2 -right-2 bg-[#822432] text-white text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
          {wishCount}
        </span>
      )}
    </button>
    <button
      className="relative hover:text-[#822432] transition-colors"
      aria-label="Bag"
    >
      <ShoppingBag size={20} strokeWidth={1.6} />
      <span className="absolute -top-2 -right-2 bg-[#1A1A1A] text-white text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center">
        {bagCount}
      </span>
    </button>
  </div>
);

const MobileDrawer = ({ open, onClose }) => (
  <div
    className={`lg:hidden fixed inset-0 z-50 transition-all duration-400 ${
      open ? "visible" : "invisible"
    }`}
  >
    <div
      className={`absolute inset-0 bg-black/40 transition-opacity duration-400 ${
        open ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
    />
    <div
      className={`absolute left-0 top-0 h-full w-[82%] max-w-sm bg-white p-7 transition-transform duration-400 ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex justify-between items-center mb-10">
        <Logo className="h-12 w-auto object-contain" />
        <button onClick={onClose} aria-label="Close">
          <X size={24} strokeWidth={1.5} />
        </button>
      </div>
      <nav className="flex flex-col gap-6">
        {navLinks.map((l) => (
          <a
            key={l.label}
            href={l.href}
            onClick={onClose}
            className="font-body text-xl font-medium"
          >
            {l.label}
          </a>
        ))}
      </nav>
    </div>
  </div>
);

const Header = ({ bagCount = 0, wishCount = 0 }) => {
  const scrolled = useScrolled(SCROLL_THRESHOLD);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-[0_1px_20px_rgba(0,0,0,0.06)] py-2"
          : "bg-white py-2.5"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-8 flex items-center justify-between">
        <DesktopNav />
        <button
          className="lg:hidden text-[#1A1A1A]"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} strokeWidth={1.5} />
        </button>
        <a
          href="#top"
          className="absolute left-1/2 -translate-x-1/2 flex items-center"
        >
          <Logo className="h-12 md:h-16 w-auto object-contain" />
        </a>
        <HeaderIcons bagCount={bagCount} wishCount={wishCount} />
      </div>

      <MobileDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
    </header>
  );
};

export default Header;
